"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function formatDate(dateStr?: string) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  } catch {
    return "";
  }
}
function stripHtml(html?: string) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
}
function JournalPostsConsumer({
  revealed = false
}: {
  revealed?: boolean;
}) {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="divide-y divide-border">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="py-6 first:pt-0 last:pb-0">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
              <div className="h-5 w-2/3 bg-muted/60 rounded animate-pulse" />
              <div className="h-3 w-24 bg-muted/60 rounded animate-pulse" />
            </div>
            <div className="mt-3 h-4 w-4/5 bg-muted/60 rounded animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground font-default">
        Unable to load research insights at this moment.
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-sm text-muted-foreground font-default">
        No research insights to display yet.
      </div>;
  }
  return <div className="divide-y divide-border">
      {posts.map((post, i) => {
      const excerpt = stripHtml(post.excerpt);
      const dateLabel = formatDate(post.date);
      return <article key={post.id} data-index={i} className={`py-6 first:pt-0 last:pb-0 transition-all duration-700 ease-out ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
        transitionDelay: `${i * 120}ms`
      }}>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6">
              <h3 className="font-default text-lg font-medium text-foreground md:text-xl">
                <Link to={post.link || "#"} className="hover:underline hover:text-[#FFDB99] transition-colors">
                  {post.title}
                </Link>
              </h3>
              {dateLabel && <time className="shrink-0 text-xs text-muted-foreground font-default">
                  {dateLabel}
                </time>}
            </div>
            {excerpt && <p className="mt-2 line-clamp-1 text-sm text-muted-foreground font-default">
                {excerpt}
              </p>}
          </article>;
    })}
    </div>;
}
export default function LatestInsights() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll();
  const {
    ref: postsRef,
    isVisible: postsVisible
  } = useRevealOnScroll();
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date",
    ignore_sticky: true
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="18" id="latest-insights" className="bg-background py-24 md:py-32 lg:py-[140px] dark">
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <span className="text-[11px] font-medium uppercase font-default" style={{
            color: "var(--primary)",
            letterSpacing: "0.22em"
          }}>
              Research & Insights
            </span>
            <h2 className="mt-5 font-default text-4xl leading-[1.05] text-foreground md:text-5xl lg:text-[52px]">
              What we're learning about how marks are lost.
            </h2>
          </div>

          <div ref={rightRef} className={`lg:col-span-5 lg:col-start-8 lg:pt-4 transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: "150ms"
        }}>
            <p className="text-base leading-relaxed text-muted-foreground font-default md:text-lg">
              Deep dives into assessment logic, process-based diagnostic methodologies, and the evolving landscape of educational infrastructure.
            </p>
            <Link to="/InsightsPage" className="group mt-8 inline-flex items-center gap-2 text-xs font-medium uppercase font-default" style={{
            color: "var(--primary)",
            letterSpacing: "0.2em"
          }}>
              View All Research
              <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-out group-hover:translate-x-1.5" />
            </Link>
          </div>
        </div>

        <div className="mt-16 h-px w-full md:mt-20" style={{
        backgroundColor: "var(--border)"
      }} aria-hidden="true" />

        <div ref={postsRef} className="mt-16 md:mt-20">
          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 3,
          order: "desc",
          orderby: "date",
          ignore_sticky: true,
          status: ["publish"]
        }} revealed={true} postsVisible={true} as={true} any={true}>
            <JournalPostsConsumer revealed={postsVisible} />
          </PostsProvider>
        </div>

        <div className="mt-12 flex justify-center lg:hidden">
          <Button asChild variant="ghost" className="font-default">
            <Link to="/InsightsPage" className="inline-flex items-center gap-2">
              View All Research
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}